import axios from 'axios';

import { baseAPI } from '~shared/api/base-api';

export interface ExportPerformanceLineupParams {
  showId: string;
  format?: 'csv' | 'pdf';
}

export const exportPerformanceLineup = async ({
  showId,
  format = 'csv',
}: ExportPerformanceLineupParams): Promise<Blob> => {
  const response = await baseAPI.get(
    `/shows/${showId}/reports/lineup`,
    {
      params: { format },
      responseType: 'blob',
    },
  );
  return response.data;
};

const normalizeMessage = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const extractErrorMessage = async (error: unknown): Promise<string | null> => {
  if (axios.isAxiosError(error) && error.response) {
    const { data } = error.response;

    if (data instanceof Blob) {
      try {
        const text = (await data.text()).trim();
        if (!text) {
          return null;
        }

        try {
          const parsed = JSON.parse(text) as Record<string, unknown>;
          const message =
            normalizeMessage(parsed.message) ??
            normalizeMessage(parsed.error);
          return message ?? text;
        } catch {
          return text;
        }
      } catch {
        return null;
      }
    }

    if (typeof data === 'string' && data.trim()) {
      return data.trim();
    }

    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;
      const message =
        normalizeMessage(record.message) ?? normalizeMessage(record.error);
      if (message) {
        return message;
      }
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  return null;
};

export const downloadPerformanceLineup = async (
  showId: string,
  format: 'csv' | 'pdf' = 'csv',
): Promise<void> => {
  try {
    const blob = await exportPerformanceLineup({ showId, format });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `performance-lineup-${showId}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    const message = await extractErrorMessage(error);
    if (message) {
      throw new Error(message);
    }
    throw error;
  }
};
